import React, { useEffect, useRef } from "react";
import { useUserContext } from "../context/UserContext";
import { IUser } from "../context/interfaces/User";

export const loginRedirect = () => {
  const returnUrl = encodeURIComponent(window.location.href);
  if (window.location.pathname.startsWith("/.auth/")) return;
  window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
};

type CheckAuthOpts = { redirectOn401?: boolean };

export const checkAuthentication = async (
  onSuccess?: (user: IUser) => void,
  opts: CheckAuthOpts = {},
): Promise<void> => {
  const { redirectOn401 = false } = opts;

  try {
    const response = await fetch(`/api/auth/`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
      redirect: "manual",
    });

    const isOpaqueRedirect = response.type === "opaqueredirect";

    // Handle 403 with redirect_to (user not in allowed groups)
    if (response.status === 403) {
      try {
        const errorData = await response.json();
        if (errorData?.redirect_to) {
          window.location.href = errorData.redirect_to;
          return;
        }
      } catch {
        // If JSON parsing fails, treat as regular 403
      }
    }

    const isUnauth =
      response.status === 401 || response.status === 403 || isOpaqueRedirect;

    if (isUnauth && redirectOn401) {
      loginRedirect();
      return;
    }
    if (!response.ok) return;

    const raw = await response.text();
    const cleaned = raw.replace(/^\)\]\}',?\s*/, "");
    const body: any = JSON.parse(cleaned);

    onSuccess?.({
      id: body.id,
      name: body.name,
      email: body.email,
      is_superuser: !!body?.is_superuser,
      department: body.department,
      location: body.location,
      avatar: body.avatar,
      job_title: body.job_title,
      settings: body?.settings,
      roles: body?.roles ?? [],
    });
    // Only persist server-provided settings to localStorage when an
    // explicit onSuccess handler was provided (initial auth flow).
    // This prevents background auth checks (e.g. axios interceptors)
    // from clobbering user-modified settings in the browser.
    if (typeof onSuccess === "function") {
      localStorage.setItem(
        "settings",
        JSON.stringify({
          country: body.settings?.country ?? [],
          regulator: body.settings?.regulator ?? [],
          classes: body.settings?.classes ?? [],
        }),
      );
      window.dispatchEvent(new Event("settings:updated"));
    }
  } catch {
    return;
  }
};

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { setUser } = useUserContext();
  const isLocalhost = /^https?:\/\/localhost(?::\d+)?$/.test(
    window.location.origin,
  );

  useEffect(() => {
    if (isLocalhost) return;

    checkAuthentication(setUser, { redirectOn401: false });
  }, [isLocalhost, setUser]);

  return <>{children}</>;
};

export default AuthGuard;
